import { skipToken } from '@apollo/client/react'
import { bypassAdmin } from 'src/stores/admin'
import { useCheckTokenForArgs } from 'src/utils/token'
import { cache } from './client'
import type { User } from './generated/graphql'
import {
  GenCodeDocument,
  GetMyExternalProfilesDocument,
  GetMyInfoDocument,
  GetUserWithExternalProfilesDocument,
  LoginDocument,
  RefreshTokenDocument,
  RegisterDocument,
  SetPushTokenDocument,
  SetupMfaDocument,
  UnlinkExternalDocument,
  UpdateMyInfoDocument,
  UpdateUserSensitiveInfosDocument,
  VerifyEmailDocument,
  WithdrawAccountDocument,
} from './generated/graphql'
import {
  createMutationHelper,
  createQueryHook,
  createRefetchQueryHelper,
  modifyQueryHook,
} from './utils'

export const [useRegister, executeRegister] =
  createMutationHelper(RegisterDocument)
export const [useLogin, executeLogin] = createMutationHelper(LoginDocument)
export const [useRefreshToken, executeRefreshToken] =
  createMutationHelper(RefreshTokenDocument)
export const [useGenCode, executeGenCode] =
  createMutationHelper(GenCodeDocument)
export const [useVerifyEmail, executeVerifyEmail] =
  createMutationHelper(VerifyEmailDocument)

const useGetMyInfo = modifyQueryHook(createQueryHook(GetMyInfoDocument), {
  reduce: r => {
    return r?.me && bypassAdmin() ? { ...r?.me, isAdmin: false } : r?.me
  },
})

export const useMyInfo = (options?: Parameters<typeof useGetMyInfo>[1]) => {
  const input = useCheckTokenForArgs({})
  const res = useGetMyInfo(input ?? skipToken, options)

  // Temporary patch — Fixes logging out causing loading:true forever
  // After logout, resetStore() triggers a GetMyInfo refetch (returning 401)
  // while React simultaneously transitions this query to skipToken. skipToken
  // sets fetchPolicy to "standby" internally, which stops the observable from
  // emitting. The in-flight refetch's loading:true snapshot is captured at
  // transition time and never cleared, leaving loading stuck at true.
  if (!input) return { ...res, loading: false }
  return res
}
export const refetchGetMyInfo = createRefetchQueryHelper(GetMyInfoDocument)

export const updateGetUserInfo = (newData?: User) => {
  if (newData && bypassAdmin()) {
    newData = { ...newData, isAdmin: false }
  }
  if (newData) {
    cache.updateQuery({ query: GetMyInfoDocument }, data => {
      return { ...data, me: { ...data?.me, ...newData } }
    })
  }
  refetchGetMyInfo()
}

export const [useUpdateMyInfo, executeUpdateMyInfo] = createMutationHelper(
  UpdateMyInfoDocument,
  {
    update: (cache, result) => {
      updateGetUserInfo(result.data?.updateUserInfo)
    },
  },
)

export const [useUpdateUserSensitiveInfos, executeUpdateUserSensitiveInfos] =
  createMutationHelper(UpdateUserSensitiveInfosDocument)

export const [useSetPushToken, executeSetPushToken] =
  createMutationHelper(SetPushTokenDocument)

export const [useSetupMFA, executeSetupMFA] =
  createMutationHelper(SetupMfaDocument)

export const [useWithdrawAccount, executeWithdrawAccount] =
  createMutationHelper(WithdrawAccountDocument)

export const useGetMyExternalProfiles = modifyQueryHook(
  createQueryHook(GetMyExternalProfilesDocument),
  {
    reduce: r => r?.me?.externalProfiles,
  },
)
export const [useUnlinkExternal, executeUnlinkExternal] = createMutationHelper(
  UnlinkExternalDocument,
)

export const useGetUserWithExternalProfiles = modifyQueryHook(
  createQueryHook(GetUserWithExternalProfilesDocument),
  {
    transform: (userId: string) => ({ userId }),
    reduce: r => r?.user,
  },
)
